/**
 * AI Chat Panel
 *
 * Refinement chat sidebar for iterating on AI presentations.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAI } from './context/AIContext';

interface AIChatPanelProps {
  isOpen: boolean;
  onClose?: () => void;
}

export function AIChatPanel({ isOpen, onClose }: AIChatPanelProps) {
  const {
    messages,
    refine,
    isLoading,
    error,
    clearError,
    presentation,
    selectedScreenIndex,
  } = useAI();

  const [input, setInput] = useState('');
  const [focusOnScreen, setFocusOnScreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!input.trim() || isLoading) return;

      const message = input;
      setInput('');
      clearError();

      await refine(message, focusOnScreen ? selectedScreenIndex : undefined);
    },
    [input, isLoading, refine, clearError, focusOnScreen, selectedScreenIndex]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  if (!isOpen) return null;

  const screenName = presentation?.screens[selectedScreenIndex]?.screenType || 'slide';

  return (
    <div className="ai-chat-panel">
      <div className="ai-chat-header">
        <h3>flowboardAI</h3>
        {onClose && (
          <button className="ai-chat-close" onClick={onClose}>
            &times;
          </button>
        )}
      </div>

      <div className="ai-chat-messages">
        {messages.length === 0 && (
          <div className="ai-chat-empty">
            <p>Ask me to make changes to your presentation.</p>
            <div className="ai-chat-suggestions">
              <button onClick={() => setInput('Make the title more impactful')}>
                Make the title more impactful
              </button>
              <button onClick={() => setInput('Add more bullet points')}>
                Add more bullet points
              </button>
              <button onClick={() => setInput('Add a call-to-action slide')}>
                Add a call-to-action slide
              </button>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`ai-chat-message ai-chat-${message.role}`}
          >
            <div className="ai-chat-avatar">
              {message.role === 'user' ? 'You' : 'AI'}
            </div>
            <div className="ai-chat-content">{message.content}</div>
          </div>
        ))}

        {isLoading && (
          <div className="ai-chat-message ai-chat-assistant ai-chat-loading">
            <div className="ai-chat-avatar">AI</div>
            <div className="ai-chat-content">
              <span className="ai-typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="ai-chat-error">
          <span>Error: {error}</span>
          <button onClick={clearError}>&times;</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="ai-chat-input-form">
        {presentation && presentation.screens.length > 1 && (
          <label className="ai-chat-focus-toggle">
            <input
              type="checkbox"
              checked={focusOnScreen}
              onChange={(e) => setFocusOnScreen(e.target.checked)}
            />
            Focus on {screenName} (slide {selectedScreenIndex + 1})
          </label>
        )}

        <div className="ai-chat-input-row">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you'd like to change..."
            rows={2}
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="ai-chat-send"
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                fill="currentColor"
                d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"
              />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
}

export default AIChatPanel;
